import webrtcAdapter from 'webrtc-adapter';
import {
  ActiveMediaDevices,
  EnumeratedMediaDevices,
  MediaDevicesPermissions,
  PermissionStatus,
  UnwrappedMediaDeviceInfo,
} from 'features/user-media/types';
import { getRelatedDevice } from 'features/user-media/utils/getRelatedDevice';
import { getDefaultPromptedDevice } from 'features/user-media/utils/getDefaultPromptedDevice';
import { userAgentDetails } from 'utils/userAgentDetails';

export type DefaultDevice = string | undefined;

export const hasMediaDevicesSupport = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices;

export const hasDisplayMediaSupport = () =>
  hasMediaDevicesSupport() && typeof navigator.mediaDevices.getDisplayMedia === 'function';

export const addDeviceChangeListener = (listener: EventListenerOrEventListenerObject) => {
  if (!hasMediaDevicesSupport()) {
    return;
  }

  navigator.mediaDevices.addEventListener('devicechange', listener);
};

export const removeDeviceChangeListener = (listener: EventListenerOrEventListenerObject) => {
  if (!hasMediaDevicesSupport()) {
    return;
  }

  navigator.mediaDevices.removeEventListener('devicechange', listener);
};

export const requireMediaDevices = (): MediaDevices => {
  if (!hasMediaDevicesSupport()) {
    throw new DOMException('navigator.mediaDevices is unavailable', 'NotAllowedError');
  }

  return navigator.mediaDevices;
};

export async function enumerateDevices() {
  const result: EnumeratedMediaDevices = {
    audioinput: [],
    videoinput: [],
    audiooutput: [],
  };

  if (!hasMediaDevicesSupport()) {
    return result;
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  const systemInfo = userAgentDetails();

  const isAndroidWebview = systemInfo.browser.name === 'Chrome WebView';
  const isIOS = systemInfo.os.name === 'iOS';

  for (const device of devices) {
    const deviceKind = device.kind;

    const skipAndroidDefaultMic =
      isAndroidWebview &&
      device.deviceId === 'default' &&
      deviceKind === 'audioinput' &&
      !device.label;

    // iOS controls audio routing at the OS level based on which microphone
    // is active (the active mic's bidirectional audio route), not by setSinkId
    // filter out audiooutput devices to prevent misleading users with non-functional speaker selection
    const skipIOSAudioOutput = isIOS && deviceKind === 'audiooutput';

    if (!skipAndroidDefaultMic && !skipIOSAudioOutput) {
      result[deviceKind].push({
        deviceId: device.deviceId,
        groupId: device.groupId,
        kind: device.kind,
        label: device.label,
      });
    }
  }

  return result;
}

export function stopStreamTracks(mediaStream: MediaStream | null) {
  if (!mediaStream) {
    return;
  }

  const tracks = mediaStream.getTracks();
  tracks.forEach((mst) => {
    mst.stop();
  });
}

export const isMediaDeviceAvailable = (
  mediaDevices: EnumeratedMediaDevices,
  kind: MediaDeviceKind
) => !!mediaDevices[kind].length;

export const isDeviceGivenPermission = (
  mediaDevices: EnumeratedMediaDevices,
  kind: MediaDeviceKind
) => !!mediaDevices[kind][0]?.label;

export const getDevicePermissions = (
  mediaDevices: EnumeratedMediaDevices,
  kind: MediaDeviceKind,
  initialSetup?: boolean
): PermissionStatus => {
  const isAvailable = isMediaDeviceAvailable(mediaDevices, kind);

  if (!isAvailable) {
    return 'not-found';
  }

  if (webrtcAdapter.browserDetails.browser === 'safari') {
    if (initialSetup) {
      return 'prompt';
    }
  }

  // if user disabled device or denied permissions, device 'label' will be blank
  return isDeviceGivenPermission(mediaDevices, kind) ? 'granted' : 'denied';
};

export const updateMediaDevicesPermissions = (
  mediaDevices: EnumeratedMediaDevices,
  initialSetup: boolean
): MediaDevicesPermissions => {
  const permissions: MediaDevicesPermissions = {};

  for (const kind of Object.keys(mediaDevices) as MediaDeviceKind[]) {
    permissions[kind] = getDevicePermissions(mediaDevices, kind, initialSetup);
  }

  return permissions;
};

export const getDefaultMediaDevice = (
  mediaDevices: EnumeratedMediaDevices,
  kind: MediaDeviceKind
): DefaultDevice => {
  const isAvailable = isMediaDeviceAvailable(mediaDevices, kind);

  if (!(isAvailable && isDeviceGivenPermission(mediaDevices, kind))) {
    return undefined;
  }

  const targetDevice = mediaDevices[kind].find((device) => device.deviceId);
  if (!targetDevice) {
    return undefined;
  }

  const defaultDevice = getRelatedDevice(mediaDevices[kind], targetDevice);

  return defaultDevice.deviceId;
};

export const prepareActiveDevices = (mediaDevices: EnumeratedMediaDevices): ActiveMediaDevices => {
  const activeDevices: ActiveMediaDevices = {};
  const keys = Object.keys(mediaDevices) as MediaDeviceKind[];

  for (const kind of keys) {
    activeDevices[kind] = getDefaultPromptedDevice(mediaDevices, kind);
  }

  return activeDevices;
};

// Determines the requested user media
export const getBaseMediaConstraints = (
  mediaDevices: EnumeratedMediaDevices,
  permissions: { audio: boolean; video: boolean }
) => ({
  audio: permissions.audio === false ? false : isMediaDeviceAvailable(mediaDevices, 'audioinput'),
  video: permissions.video === false ? false : isMediaDeviceAvailable(mediaDevices, 'videoinput'),
});

export const getDefaultActiveDevices = (mediaDevices: EnumeratedMediaDevices) => ({
  audioinput: getDefaultPromptedDevice(mediaDevices, 'audioinput'),
  videoinput: getDefaultPromptedDevice(mediaDevices, 'videoinput'),
  audiooutput: getDefaultPromptedDevice(mediaDevices, 'audiooutput'),
});

export const getDevicePromptConstraints = (
  mediaDevices: EnumeratedMediaDevices,
  permissions: { audio: boolean; video: boolean }
) => {
  const getAudioConstraints = () => {
    if (permissions.audio === false) {
      return false;
    }

    if (!isMediaDeviceAvailable(mediaDevices, 'audioinput')) {
      return false;
    }

    const deviceId = getDefaultPromptedDevice(mediaDevices, 'audioinput');
    if (deviceId === undefined) {
      return false;
    }

    return {
      deviceId: {
        exact: deviceId,
      },
    };
  };

  const getVideoConstraints = () => {
    if (permissions.video === false) {
      return false;
    }

    if (!isMediaDeviceAvailable(mediaDevices, 'videoinput')) {
      return false;
    }

    const deviceId = getDefaultPromptedDevice(mediaDevices, 'videoinput');
    if (deviceId === undefined) {
      return false;
    }

    return {
      deviceId: {
        exact: deviceId,
      },
    };
  };

  return {
    audio: getAudioConstraints(),
    video: getVideoConstraints(),
  };
};

export const findDeviceByLabelOrId = (devices: UnwrappedMediaDeviceInfo[], identifier: string) =>
  devices.find((device) => device.label === identifier || device.deviceId === identifier);
